/**
 * Token-2022 Confidential Transfer recognizer (display-only).
 *
 * X1 mainnet has SPL Token-2022 (`TokenzQdBN…`) + the ZK ElGamal proof program
 * (`ZkE1Gama1Proof…`, executable) live, so ConfidentialTransfer works on-chain.
 * A ConfidentialTransfer instruction is a Token-2022 instruction whose first
 * data byte is `0x1b` (27 = ConfidentialTransferExtension); the second byte is
 * the sub-instruction (SPL `ConfidentialTransferInstruction` enum).
 *
 * This module RECOGNIZES those instructions and — for the public bridge ops
 * (Deposit / Withdraw) — decodes the plaintext amount that is genuinely on
 * chain. It NEVER attempts to reveal a Transfer amount: that value is ElGamal
 * ciphertext + ZK proof and is not present in the instruction data. The point
 * of the explorer feature is to show, honestly, what is public vs hidden.
 *
 * Verified against real X1 mainnet transactions (see confidentialTransfer.test):
 *   Transfer  1b 07 …(167 bytes ciphertext/proof)…  -> amount HIDDEN
 *   Deposit   1b 05 00e1f505 00000000 06            -> 100.0 (6dp) PUBLIC
 *   Withdraw  1b 06 40787d01 00000000 06 …          -> 25.0  (6dp) PUBLIC
 *   Configure 1b 02 …                               -> account setup
 */
import bs58 from 'bs58'

export const TOKEN_2022_PROGRAM_ID = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb'
export const ZK_ELGAMAL_PROOF_PROGRAM_ID = 'ZkE1Gama1Proof11111111111111111111111111111'

/** Token-2022 top-level instruction: ConfidentialTransferExtension. */
const CT_EXTENSION_DISCRIMINATOR = 0x1b // 27
/** Token-2022 top-level instruction: Reallocate (used during CT account setup). */
const REALLOCATE_DISCRIMINATOR = 0x1d // 29

/** How the amount relates to on-chain visibility for a given sub-instruction. */
export type CtVisibility = 'hidden' | 'public' | 'setup' | 'other'

/** SPL ConfidentialTransferInstruction sub-instruction → friendly name + visibility. */
const CT_SUBTYPES: Record<number, { name: string; visibility: CtVisibility }> = {
  0: { name: 'Initialize Confidential Mint', visibility: 'setup' },
  1: { name: 'Update Confidential Mint', visibility: 'setup' },
  2: { name: 'Configure Confidential Account', visibility: 'setup' },
  3: { name: 'Approve Confidential Account', visibility: 'setup' },
  4: { name: 'Empty Confidential Account', visibility: 'other' },
  5: { name: 'Confidential Deposit', visibility: 'public' },
  6: { name: 'Confidential Withdraw', visibility: 'public' },
  7: { name: 'Confidential Transfer', visibility: 'hidden' },
  8: { name: 'Apply Pending Balance', visibility: 'other' },
  9: { name: 'Enable Confidential Credits', visibility: 'other' },
  10: { name: 'Disable Confidential Credits', visibility: 'other' },
  11: { name: 'Enable Non-Confidential Credits', visibility: 'other' },
  12: { name: 'Disable Non-Confidential Credits', visibility: 'other' },
  13: { name: 'Confidential Transfer (Split Proofs)', visibility: 'hidden' },
}

export interface ConfidentialAmount {
  /** Raw base-unit amount as decoded from the instruction (u64). */
  raw: bigint
  /** Token decimals byte carried in the Deposit/Withdraw instruction. */
  decimals: number
  /** Human-readable amount = raw / 10^decimals. */
  ui: string
}

export interface ConfidentialInstructionInfo {
  /** Sub-instruction byte (data[1]). */
  subType: number
  /** Friendly label used for the instruction badge. */
  name: string
  /** Whether the transferred amount is hidden, public (bridge), or n/a. */
  visibility: CtVisibility
  /** Total instruction-data length (proof/ciphertext size lives here). */
  dataLen: number
  /** Present only for Deposit/Withdraw — the genuinely-public plaintext amount. */
  amount?: ConfidentialAmount
}

/**
 * Instruction `data` arrives as a base58 string, a `[data, encoding]` tuple, or
 * (already-parsed shapes) something else entirely. Normalize to raw bytes, or
 * null when it isn't decodable base58.
 */
export function decodeIxDataBytes(rawData: unknown): Uint8Array | null {
  // web3.js `encoding: 'json'` (what our /api/rpc/transaction endpoint pins)
  // delivers instruction data as a Buffer JSON blob `{type:'Buffer',data:[…]}`
  // or a raw byte array; jsonParsed / base58 paths deliver a bs58 string (or a
  // `[data, 'base58']` pair). Handle all of them so recognition works whatever
  // the fetch path.
  if (rawData instanceof Uint8Array) return rawData
  if (
    rawData &&
    typeof rawData === 'object' &&
    (rawData as any).type === 'Buffer' &&
    Array.isArray((rawData as any).data)
  ) {
    return Uint8Array.from((rawData as any).data)
  }
  if (Array.isArray(rawData) && rawData.every((n) => typeof n === 'number')) {
    return Uint8Array.from(rawData as number[])
  }
  const s =
    typeof rawData === 'string'
      ? rawData
      : Array.isArray(rawData) && typeof rawData[0] === 'string'
        ? rawData[0]
        : ''
  if (!s) return null
  try {
    return bs58.decode(s)
  } catch {
    return null
  }
}

function formatUi(raw: bigint, decimals: number): string {
  if (decimals <= 0) return raw.toString()
  const s = raw.toString().padStart(decimals + 1, '0')
  const whole = s.slice(0, s.length - decimals)
  const frac = s.slice(s.length - decimals).replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : whole
}

/**
 * Recognize a Token-2022 ConfidentialTransfer instruction from its program id +
 * raw instruction data. Returns null for anything that isn't one (including the
 * Reallocate setup step, which is Token-2022 but not itself confidential).
 */
export function recognizeConfidentialInstruction(
  programId: string,
  rawData: unknown
): ConfidentialInstructionInfo | null {
  if (programId !== TOKEN_2022_PROGRAM_ID) return null
  const bytes = decodeIxDataBytes(rawData)
  if (!bytes || bytes.length < 2) return null
  if (bytes[0] !== CT_EXTENSION_DISCRIMINATOR) return null

  const subType = bytes[1]
  const sub = CT_SUBTYPES[subType] ?? { name: 'Confidential Transfer (Extension)', visibility: 'other' as CtVisibility }

  const info: ConfidentialInstructionInfo = {
    subType,
    name: sub.name,
    visibility: sub.visibility,
    dataLen: bytes.length,
  }

  // Deposit (5) / Withdraw (6): u64 LE amount at offset 2, decimals byte at 10.
  // These are the PUBLIC bridge ops — the amount is genuinely on chain.
  if ((subType === 5 || subType === 6) && bytes.length >= 11) {
    let raw = 0n
    for (let i = 0; i < 8; i++) raw |= BigInt(bytes[2 + i]) << BigInt(8 * i)
    const decimals = bytes[10]
    info.amount = { raw, decimals, ui: formatUi(raw, decimals) }
  }

  return info
}

/** True for the Token-2022 Reallocate op that accompanies confidential-account setup. */
export function isTokenReallocate(programId: string, rawData: unknown): boolean {
  if (programId !== TOKEN_2022_PROGRAM_ID) return false
  const bytes = decodeIxDataBytes(rawData)
  return !!bytes && bytes.length >= 1 && bytes[0] === REALLOCATE_DISCRIMINATOR
}

/** Fallback recognizer from log messages when raw ix data isn't available. */
export function confidentialNameFromLogs(logMessages?: string[]): string | null {
  if (!logMessages) return null
  for (const l of logMessages) {
    const m = l.match(/ConfidentialTransferInstruction::(\w+)/)
    if (m) {
      // Map the enum variant name to our friendly label where we have one.
      const byName: Record<string, string> = {
        ConfigureAccount: 'Configure Confidential Account',
        Deposit: 'Confidential Deposit',
        Withdraw: 'Confidential Withdraw',
        Transfer: 'Confidential Transfer',
        ApplyPendingBalance: 'Apply Pending Balance',
      }
      return byName[m[1]] ?? `Confidential ${m[1]}`
    }
  }
  return null
}

/**
 * Identify the token accounts whose balance change is HIDDEN because a private
 * Confidential Transfer moved an encrypted amount through them.
 *
 * A private CT `Transfer` (sub 7) / `TransferWithSplitProofs` (sub 13) never
 * touches the SPL token account's public `amount`, so pre/post token balances
 * show a Change of 0 even though a real amount moved. This returns the accountKey
 * indexes (and mints) of the accounts that participate in such a transfer so the
 * UI can label their 0-change as "Encrypted" rather than a misleading "-".
 *
 * ONLY the amount-hidden transfers qualify. Deposit/Withdraw carry a real public
 * `amount` and already show a correct nonzero Change — they are deliberately
 * excluded (visibility !== 'hidden').
 *
 * Precise by construction: only accounts actually referenced by the private
 * transfer instruction are returned, so an unrelated 0-change account in the
 * same transaction is never flagged.
 */
export interface ConfidentialTransferParticipants {
  accountIndexes: Set<number>
  mints: Set<string>
  hasHiddenTransfer: boolean
}

export function confidentialTransferParticipants(
  instructions: any[] | undefined | null,
  accountKeys: string[] | undefined | null
): ConfidentialTransferParticipants {
  const accountIndexes = new Set<number>()
  const mints = new Set<string>()
  const keys = accountKeys || []
  for (const ix of instructions || []) {
    if (!ix) continue
    const pidx = ix.programIdIndex
    const programId =
      typeof pidx === 'number' ? keys[pidx] : (ix.programId ?? ix.program ?? '')
    const info = recognizeConfidentialInstruction(programId, ix.data)
    if (!info || info.visibility !== 'hidden') continue // private Transfer only
    const accts: unknown = ix.accountKeyIndexes ?? ix.accounts
    if (!Array.isArray(accts)) continue
    for (const a of accts) if (typeof a === 'number') accountIndexes.add(a)
    // Transfer account layout: [source, mint, destination, …] → mint at index 1.
    if (accts.length >= 2 && typeof accts[1] === 'number') {
      const m = keys[accts[1] as number]
      if (m) mints.add(m)
    }
  }
  return { accountIndexes, mints, hasHiddenTransfer: accountIndexes.size > 0 }
}
